/**
 * Factor expiry banner — shown at the top of the SPA whenever a factor is EXPIRING_SOON or EXPIRED.
 * Dismissible per session (sessionStorage). Multiple expiring factors stack banners.
 *
 * Integrated in AppComponent, similar to the cosession active-session banner.
 */
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslocoDirective, TranslocoService, provideTranslocoScope } from '@jsverse/transloco';
import { Factor } from './factor.model';
import { FactorService } from './factor.service';
import { actingContextSkipsFactorProbe } from '../../acting-context.util';
import { AppStore } from '../../store';

const DISMISS_KEY = 'tb-factor-expiry-dismissed';

function getDismissed(): Set<string> {
  try {
    const raw = sessionStorage.getItem(DISMISS_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function saveDismissed(ids: Set<string>): void {
  try {
    sessionStorage.setItem(DISMISS_KEY, JSON.stringify([...ids]));
  } catch { /* ignore quota errors */ }
}

@Component({
  selector: 'tb-factor-expiry-banner',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoDirective, RouterLink],
  providers: [provideTranslocoScope('security')],
  template: `
    <ng-container *transloco="let t; read: 'security'">
    @for (f of visibleBanners(); track f.id) {
      <div
        class="expiry-banner"
        [attr.data-state]="f.state"
        [attr.data-testid]="'expiry-banner-' + f.id"
        role="alert"
        aria-live="assertive"
      >
        <span class="banner-icon material-symbols-outlined" aria-hidden="true">
          {{ f.state === 'EXPIRED' ? 'error' : 'schedule' }}
        </span>
        <span class="banner-text">
          @if (f.state === 'EXPIRED') {
            {{ t('expiryBanner.expiredPrefix') }} <strong>{{ typeLabel(f.type) }}</strong>{{ t('expiryBanner.expiredSuffix') }}
          } @else {
            {{ t('expiryBanner.expiringPrefix') }} <strong>{{ typeLabel(f.type) }}</strong> {{ t('expiryBanner.expiringSuffix', { when: expiryLabel(f) }) }}
          }
        </span>
        <a class="banner-action" routerLink="/security/factors" [attr.aria-label]="t('expiryBanner.renewAria', { type: typeLabel(f.type) })">
          {{ t('expiryBanner.renew') }}
        </a>
        <button
          class="banner-dismiss"
          (click)="dismiss(f.id)"
          type="button"
          [attr.aria-label]="t('expiryBanner.dismissAria', { type: typeLabel(f.type) })"
        >
          <span class="material-symbols-outlined" aria-hidden="true">close</span>
        </button>
      </div>
    }
    </ng-container>
  `,
  styles: `
    :host { display: block; }

    .expiry-banner {
      display: flex; align-items: center; gap: 0.6rem;
      padding: 0.6rem 1rem; font-size: 0.85rem; font-weight: 500;
    }
    .expiry-banner[data-state='EXPIRING_SOON'] {
      background: color-mix(in srgb, var(--tb-status-warning, #e0af68) 18%, transparent);
      border-bottom: 1px solid color-mix(in srgb, var(--tb-status-warning, #e0af68) 40%, transparent);
      color: var(--tb-status-warning, #e0af68);
    }
    .expiry-banner[data-state='EXPIRED'] {
      background: color-mix(in srgb, var(--tb-finance-negative, #f7768e) 18%, transparent);
      border-bottom: 1px solid color-mix(in srgb, var(--tb-finance-negative, #f7768e) 40%, transparent);
      color: var(--tb-finance-negative, #f7768e);
    }

    .banner-icon { font-size: 1.1rem; flex-shrink: 0; }
    .banner-text { flex: 1; color: inherit; }
    .banner-text strong { font-weight: 700; }

    .banner-action {
      text-decoration: none; font-weight: 700; white-space: nowrap;
      padding: 0.2rem 0.55rem; border-radius: 4px;
      border: 1px solid currentColor; color: inherit;
      font-size: 0.8rem;
    }
    .banner-action:hover { opacity: 0.75; }
    .banner-action:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
    }

    .banner-dismiss {
      background: none; border: none; cursor: pointer; padding: 0.15rem;
      color: inherit; display: flex; align-items: center; opacity: 0.7;
      justify-content: center; flex-shrink: 0;
      min-width: var(--tb-touch, 48px); min-height: var(--tb-touch, 48px);
    }
    .banner-dismiss:hover { opacity: 1; }
    .banner-dismiss:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
    }
    .banner-dismiss .material-symbols-outlined { font-size: 1rem; }
  `,
})
export class FactorExpiryBannerComponent implements OnInit {
  private readonly factorService = inject(FactorService);
  private readonly transloco = inject(TranslocoService);
  private readonly store = inject(AppStore);

  private readonly allFactors = signal<Factor[]>([]);
  private readonly dismissed = signal<Set<string>>(getDismissed());

  readonly visibleBanners = computed(() => {
    const dis = this.dismissed();
    return this.allFactors().filter(
      (f) => (f.state === 'EXPIRING_SOON' || f.state === 'EXPIRED') && !dis.has(f.id),
    );
  });

  ngOnInit(): void {
    // RC-F skip (live-journeys triage #3): under an acting/persona context
    // GET /api/identity/me/factors is deliberately refused (403) -- there is nothing left to
    // probe (root login already proved factor readiness). Skip the guaranteed-403 fetch.
    if (actingContextSkipsFactorProbe(this.store.user()?.agencyMode)) return;
    this.factorService.listFactors().subscribe({
      next: (list) => this.allFactors.set(list),
      error: () => { /* silent — banner is non-critical */ },
    });
  }

  typeLabel(type: string): string {
    if (type === 'PASSKEY') return this.transloco.translate('security.factorType.passkey');
    if (type === 'TOTP') return this.transloco.translate('security.factorType.totp');
    return this.transloco.translate('security.factorType.hardwareToken');
  }

  expiryLabel(f: Factor): string {
    if (!f.expiresAt) return this.transloco.translate('security.expiryBanner.soon');
    const d = new Date(f.expiresAt);
    const days = Math.ceil((d.getTime() - Date.now()) / 86_400_000);
    if (days <= 0) return this.transloco.translate('security.expiryBanner.today');
    if (days === 1) return this.transloco.translate('security.expiryBanner.tomorrow');
    return this.transloco.translate('security.expiryBanner.inDays', { count: days });
  }

  dismiss(id: string): void {
    const next = new Set(this.dismissed());
    next.add(id);
    saveDismissed(next);
    this.dismissed.set(next);
  }
}
